import styles from "./add-courses-modal.module.scss";
import Button from "components/base/button/button";
import ButtonClose from "components/partial/button-close/button-close";
import { createModalContext, ModalComponent } from "components/base/modal-dialog/modal-dialog";
import { CoursesApi } from "api/controllers/CoursesApi";
import { IOptionsItem } from "helpers/select.helper";
import { IQueryParams } from "models/dto/ZoomiLxp/Models/Query/IQueryParams";
import { ICmsCourseModel } from "models/dto/ZoomiLxp/Models/Courses/ICmsCourseModel";
import { GroupBase } from "react-select";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";
import { useState } from "react";
import { defaultDropdownStyles } from "components/base/dropdown-list/data";
import { ReactComponent as RemoveIcon } from "assets/icons/ic_close.svg";
import classNames from "classnames";

interface AddCoursesModalProps {
	excludedCourseIds: number[];
}

const { modalContext, useModalContext } = createModalContext<ICmsCourseModel[]>();

const initParams: IQueryParams = {
	take: 100,
	skip: 0,
	filterCriteria: [],
	sortCriteria: [
		{
			order: 1,
			propertyName: "title",
			direction: 0,
		},
	],
	queryString: "",
};

const toOption = (course: ICmsCourseModel): IOptionsItem & { course: ICmsCourseModel } => ({
	value: course.id,
	label: course.title,
	course,
});

const AddCoursesModalComponent = ({ excludedCourseIds }: AddCoursesModalProps) => {
	const { resolve } = useModalContext();
	const [selectedCourses, setSelectedCourses] = useState<ICmsCourseModel[]>([]);
	const excludedIds = new Set([...excludedCourseIds, ...selectedCourses.map(({ id }) => id)]);

	const loadOptions: LoadOptions<any, GroupBase<any>, any> = async (search, prevOptions, { skip }) => {
		const params = { ...initParams, skip, queryString: search };
		const response = await CoursesApi.getAllCourses(params);
		const records = response.data.data.records.filter(({ id }) => !excludedIds.has(id));

		return {
			options: records.map(toOption),
			hasMore: response.data.data.totalFilteredRecords > prevOptions.length + Number(params.take),
			additional: {
				skip: skip + params.take,
			},
		};
	};

	const handleChange = (option: (IOptionsItem & { course: ICmsCourseModel }) | null) => {
		if (!option || selectedCourses.some(({ id }) => id === Number(option.value))) return;
		setSelectedCourses((courses) => [...courses, option.course]);
	};

	const handleRemove = (courseId: number) => {
		setSelectedCourses((courses) => courses.filter(({ id }) => id !== courseId));
	};

	return (
		<div className={styles.add_courses_modal}>
			<div className={styles.add_courses_modal__header}>
				<h2 className={styles.add_courses_modal__title}>Add Courses</h2>
				<ButtonClose onClick={() => resolve()} />
			</div>
			<div className={styles.add_courses_modal__body}>
				<AsyncPaginate
					additional={{ skip: 0 }}
					debounceTimeout={300}
					value={null}
					loadOptions={loadOptions}
					placeholder="Select Course"
					styles={defaultDropdownStyles}
					className={styles.add_courses_modal__select}
					onChange={handleChange}
				/>
				<div className={styles.add_courses_modal__chips_block}>
					{selectedCourses.map((course) => (
						<Button
							key={course.id}
							label={course.title}
							onClick={() => handleRemove(course.id)}
							size="small"
							iconPosition="right"
							icon={<RemoveIcon />}
							className={styles.add_courses_modal__remove_btn}
							labelColor="secondaryText"
						/>
					))}
				</div>
			</div>
			<Button
				label="Add"
				onClick={() => resolve(selectedCourses)}
				disabled={!selectedCourses.length}
				className={classNames(styles.add_courses_modal__add_btn, {
					[styles.add_courses_modal__add_btn_disabled]: !selectedCourses.length,
				})}
			/>
		</div>
	);
};

export const AddCoursesModal: ModalComponent<AddCoursesModalProps, ICmsCourseModel[]> = {
	component: AddCoursesModalComponent,
	modalContext,
};
